import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaCheckCircle, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import { API } from '../config/api';
import VerificationModal from '../components/VerificationModal';
import NotificationToast from '../components/NotificationToast';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  25% { background-position: 100% 50%; }
  50% { background-position: 100% 100%; }
  75% { background-position: 0% 100%; }
  100% { background-position: 0% 50%; }
`;

const floatingOrbs = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-30px) rotate(120deg); }
  66% { transform: translateY(-20px) rotate(240deg); }
`;

const HeroContainer = styled(motion.div)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  opacity: 0.8;
`;


const ContentWrapper = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1000px;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 8vw, 6rem);
  margin-bottom: 1rem;
  color: #ffffff;
  font-weight: 900;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  letter-spacing: -2px;
  /* Removed heavy shimmer animation for better performance */
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1.2rem, 4vw, 2rem);
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 3rem;
  font-weight: 300;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  letter-spacing: 2px;
`;

const TypewriterText = styled(motion.span)`
  display: inline-block;
  border-right: 2px solid #fff;
  animation: blink 1s infinite;
  
  @keyframes blink {
    0%, 50% { border-color: transparent; }
    51%, 100% { border-color: #fff; }
  }
`;

const EmailSection = styled(motion.div)`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  max-width: 500px;
  width: 100%;
`;

const EmailForm = styled.form`
  display: flex;
  gap: 1rem;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const EmailInput = styled(motion.input)`
  flex: 1;
  min-width: 250px;
  padding: 1rem 1.5rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: white;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  
  &:focus {
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  }
`;

const EmailButton = styled(motion.button)`
  background: linear-gradient(45deg, #f093fb, #f5576c);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(240, 147, 251, 0.4);
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(240, 147, 251, 0.6);
  }
`;

const EmailText = styled(motion.p)`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  text-align: center;
  margin: 0;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 1.2rem 3rem;
  border: none;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(102, 126, 234, 0.6);
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  
  &::after {
    content: '↓';
    font-size: 1.5rem;
    animation: bounce 2s infinite;
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }
`;

// New scroll-down sections
const ContentSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeaturedBooksSection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin: 0;
  
  > div {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3.5rem);
  text-align: center;
  margin-bottom: 3rem;
  color: white;
  font-weight: 900;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const BooksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 320px));
  gap: 2rem;
  margin-top: 3rem;
  justify-content: center;
`;

const BookCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(45deg, #667eea, #764ba2);
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15);
  }
`;

const BookTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3436;
  margin-bottom: 1rem;
`;

const BookDescription = styled.p`
  color: #636e72;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const BookGenre = styled.span`
  display: inline-block;
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const AboutPreviewSection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #764ba2 0%, #f093fb 100%);
  color: white;
  text-align: center;
  margin: 0;
  
  > div {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const AboutContent = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const AboutText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const TestimonialsSection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  margin: 0;
  
  > div {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  
  &::before {
    content: '"';
    position: absolute;
    top: 10px;
    left: 20px;
    font-size: 4rem;
    color: #667eea;
    font-family: serif;
  }
`;

const TestimonialText = styled.p`
  font-style: italic;
  line-height: 1.6;
  color: #2d3436;
  margin-bottom: 1rem;
`;

const TestimonialAuthor = styled.div`
  font-weight: 600;
  color: #667eea;
`;

const NewsletterSection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #f5576c 0%, #4facfe 100%);
  text-align: center;
  color: white;
  margin: 0;
  
  > div {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const NewsletterContent = styled(motion.div)`
  max-width: 600px;
  margin: 0 auto;
`;

const NewsletterTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 1rem;
  font-weight: 900;
`;

const NewsletterText = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const StatsSection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  text-align: center;
  margin: 0;
  
  > div {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 3rem;
  margin-top: 3rem;
`;

const StatCard = styled(motion.div)`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 900;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  opacity: 0.8;
`;

// Gallery Section Styles
const GallerySection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  margin: 0;
  
  > div {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 280px));
  gap: 1.5rem;
  margin-top: 3rem;
  justify-content: center;
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 3/4;
  background: #f8f9fa;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-height: 350px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
  }
`;

const GalleryImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      transparent 60%,
      rgba(0, 0, 0, 0.8) 100%
    );
  }
`;

const GalleryOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  z-index: 2;
  color: white;
`;

const GalleryBookTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const GalleryBookGenre = styled.span`
  background: rgba(255, 255, 255, 0.9);
  color: #2d3436;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
`;

const ViewAllGalleryButton = styled(motion.button)`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  
  &:hover {
    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
  }
`;

// Gallery Modal Styles
const GalleryModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const GalleryModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  max-width: 90vw;
  max-height: 90vh;
  width: 700px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  position: relative;
  
  @media (max-width: 768px) {
    max-height: 95vh;
    width: 95vw;
  }
`;

const GalleryModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  z-index: 10;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const GalleryModalImage = styled.img`
  width: 100%;
  height: 60vh;
  object-fit: cover;
  display: block;
  
  @media (max-width: 768px) {
    height: 50vh;
  }
`;

const GalleryModalInfo = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const GalleryModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3436;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const GalleryModalDescription = styled.p`
  color: #636e72;
  line-height: 1.6;
  margin-bottom: 2rem;
  font-size: 1rem;
`;

const GalleryModalButton = styled(motion.button)`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  align-self: flex-start;
  
  &:hover {
    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
  }
`;

const Home = () => {
  const navigate = useNavigate();
  const [displayText, setDisplayText] = useState('');
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [books, setBooks] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [displaySettings, setDisplaySettings] = useState({
    maxFeaturedBooks: 6,
    maxGalleryImages: 6
  });
  const [showAllBooks, setShowAllBooks] = useState(false);
  const [booksLoading, setBooksLoading] = useState(true);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const fullText = 'Crafting Stories That Touch Souls';
  
  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index < fullText.length) {
        setDisplayText(fullText.slice(0, index + 1));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 100);
    
    return () => clearInterval(timer);
  }, []);
  
  useEffect(() => {
    fetchBooks();
    fetchTestimonials();
    fetchDisplaySettings();
    fetchGalleryImages();
  }, []);
  
  const fetchBooks = async () => {
    try {
      const response = await fetch(`${API}/books`);
      if (response.ok) {
        const data = await response.json();
        setBooks(data);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setBooksLoading(false);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API}/testimonials`);
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const fetchDisplaySettings = async () => {
    try {
      const response = await fetch(`${API}/display-settings`);
      if (response.ok) {
        const data = await response.json();
        setDisplaySettings(data);
      }
    } catch (error) {
      console.error('Error fetching display settings:', error);
    }
  };

  const fetchGalleryImages = async () => {
    try {
      const response = await fetch(`${API}/gallery`);
      if (response.ok) {
        const data = await response.json();
        setGalleryImages(data);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    }
  };

  // Get all gallery images from the dedicated gallery system
  const getAllGalleryImages = () => {
    return galleryImages.map(image => ({
      image: image.imagePath,
      bookId: image.bookId,
      bookTitle: image.bookTitle,
      bookGenre: image.bookGenre,
      bookDescription: image.bookDescription,
      description: image.description
    }));
  };
  
  
  // Notification management
  const addNotification = (type, message, autoRemove = true, duration = 5000) => {
    const id = Date.now() + Math.random();
    const notification = { id, type, message, autoRemove, duration };
    setNotifications(prev => [...prev, notification]);
    
    if (autoRemove) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const validateEmailFormat = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      addNotification('error', 'Please enter your email address');
      return;
    }
    
    if (!validateEmailFormat(trimmedEmail)) {
      addNotification('error', 'Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.requiresVerification) {
          setPendingEmail(trimmedEmail);
          setShowVerificationModal(true);
          addNotification('success', 'Verification code sent to your email!');
        } else {
          setEmailSubmitted(true);
          addNotification('success', 'Successfully subscribed to the newsletter!');
          setTimeout(() => {
            setEmailSubmitted(false);
            setEmail('');
          }, 3000);
        }
      } else {
        addNotification('error', data.error || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      addNotification('error', 'Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationSuccess = () => {
    setEmailSubmitted(true);
    setEmail('');
    setPendingEmail('');
    addNotification('success', 'Email verified! Welcome to Winter North\'s newsletter!');
    
    setTimeout(() => {
      setEmailSubmitted(false);
    }, 3000);
  };
  
  return (
    <>
    <HeroContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
    >
      <VideoBackground
        autoPlay
        muted
        loop
        playsInline
      >
        <source src="/Roses_video.mp4" type="video/mp4" />
      </VideoBackground>
      
      <ContentWrapper>
        <Title
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          Winter North
        </Title>
        
        <Subtitle
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
        >
          <TypewriterText>{displayText}</TypewriterText>
        </Subtitle>
        
        <EmailSection
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
        >
          <EmailText
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
          >
            Join thousands of readers • Get exclusive updates & early access
          </EmailText>
          
          <EmailForm onSubmit={handleEmailSubmit}>
            <EmailInput
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              whileFocus={{ scale: 1.02 }}
            />
            <EmailButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={emailSubmitted || isSubmitting}
            >
              {emailSubmitted ? '✓ Subscribed!' : isSubmitting ? 'Sending...' : 'Join the Journey'}
            </EmailButton>
          </EmailForm>
        </EmailSection>
        
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 2.5 }}
          style={{ marginTop: '2rem' }}
        >
          <CTAButton 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
          >
            Enter My Literary World
          </CTAButton>
        </motion.div>
      </ContentWrapper>
      
      <ScrollIndicator
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.5 }}
      >
        Scroll to explore
      </ScrollIndicator>
    </HeroContainer>
    
    {/* Featured Books Section */}
    <FeaturedBooksSection>
      <div>
        <SectionTitle
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Featured Works
        </SectionTitle>
      
      <BooksGrid>
        {booksLoading ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#636e72' }}>
            Loading books...
          </div>
        ) : books.length > 0 ? (
          books.slice(0, showAllBooks ? books.length : displaySettings.maxFeaturedBooks).map((book, index) => (
            <BookCard
              key={book.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate(`/book/${book.id}/${book.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`)}
              style={{ cursor: 'pointer' }}
            >
              {book.coverImage && (
                <div style={{
                  width: '200px',
                  height: '280px',
                  backgroundImage: `url(${API}${book.coverImage})`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  borderRadius: '10px',
                  marginBottom: '1rem',
                  backgroundColor: '#f8f9fa',
                  margin: '0 auto'
                }} />
              )}
              <BookTitle>{book.title}</BookTitle>
              <BookDescription>
                {book.description}
              </BookDescription>
              <BookGenre>{book.genre || 'Fiction'}</BookGenre>
            </BookCard>
          ))
        ) : (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#636e72' }}>
            No books available yet. Check back soon!
          </div>
        )}
      </BooksGrid>
      
      {books.length > displaySettings.maxFeaturedBooks && !showAllBooks && (
        <motion.div
          style={{ textAlign: 'center', marginTop: '3rem' }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <ViewAllGalleryButton
            onClick={() => setShowAllBooks(true)}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            Show All Books ({books.length})
          </ViewAllGalleryButton>
        </motion.div>
      )}
      
      {showAllBooks && (
        <motion.div
          style={{ textAlign: 'center', marginTop: '2rem' }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <ViewAllGalleryButton
            onClick={() => setShowAllBooks(false)}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            style={{ background: 'linear-gradient(45deg, #636e72, #2d3436)' }}
          >
            Show Less
          </ViewAllGalleryButton>
        </motion.div>
      )}
      </div>
    </FeaturedBooksSection>
    
    {/* About Preview Section */}
    <AboutPreviewSection>
      <div>
        <AboutContent
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
        <SectionTitle style={{ color: 'white' }}>
          About Winter North
        </SectionTitle>
        <AboutText>
          Winter North believes that every world, no matter how fantastic, must have a heart that bleeds. She is a creator of epic fantasy sagas, meticulously building worlds with deep histories, fractured political landscapes, and magic that is both a wonder and a curse.
        </AboutText>
        <AboutText>
          At the center of these grand tapestries are the characters: heroes and heroines bound by fate, torn apart by duty, and defined by the loves that will force them to choose between their hearts and their crowns.
        </AboutText>
        <AboutText>
          From a quiet home nestled in a land of mountains and myth, she delights in forging intricate cages of prophecy and politics for her characters... and then watching them find the strength to break free.
        </AboutText>
        <CTAButton 
          whileHover={{ scale: 1.05 }} 
          whileTap={{ scale: 0.95 }}
          style={{ marginTop: '1rem' }}
          onClick={() => navigate('/about')}
        >
          Learn More About Winter
        </CTAButton>
      </AboutContent>
      </div>
    </AboutPreviewSection>
    
    
    {/* Gallery Section */}
    {getAllGalleryImages().length > 0 && (
      <GallerySection>
        <div>
          <SectionTitle
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{ color: 'white' }}
          >
            Character Gallery
          </SectionTitle>
          
          <GalleryGrid>
            {getAllGalleryImages().slice(0, displaySettings.maxGalleryImages).map((item, index) => (
              <GalleryItem
                key={`${item.bookId}-${index}`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                onClick={() => {
                  setSelectedGalleryImage({
                    ...item,
                    bookDescription: item.description || item.bookDescription
                  });
                  setShowGalleryModal(true);
                }}
              >
                <GalleryImage src={`${API}${item.image}`} />
                <GalleryOverlay>
                  <GalleryBookTitle>{item.bookTitle}</GalleryBookTitle>
                  <GalleryBookGenre>{item.bookGenre || 'Fiction'}</GalleryBookGenre>
                </GalleryOverlay>
              </GalleryItem>
            ))}
          </GalleryGrid>
          
          <motion.div
            style={{ textAlign: 'center', marginTop: '3rem' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <ViewAllGalleryButton
              onClick={() => navigate('/gallery')}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              View Full Character Gallery
            </ViewAllGalleryButton>
          </motion.div>
        </div>
      </GallerySection>
    )}

    {/* Testimonials Section */}
    {testimonials.length > 0 && (
      <TestimonialsSection>
        <div>
          <SectionTitle
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            What Readers Say
          </SectionTitle>
        
        <TestimonialsGrid>
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <TestimonialCard
              key={testimonial.id}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
              viewport={{ once: true }}
            >
              <TestimonialText>
                "{testimonial.quote}"
              </TestimonialText>
              <TestimonialAuthor>
                — {testimonial.reviewer}
                {testimonial.bookTitle && `, ${testimonial.bookTitle}`}
              </TestimonialAuthor>
              <div style={{ marginTop: '0.5rem' }}>
                <span style={{ color: '#f39c12' }}>
                  {'★'.repeat(testimonial.rating)}
                </span>
                <span style={{ color: '#ddd' }}>
                  {'★'.repeat(5 - testimonial.rating)}
                </span>
              </div>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
        </div>
      </TestimonialsSection>
    )}
    
    {/* Newsletter Section */}
    <NewsletterSection>
      <div>
        <NewsletterContent
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
        <NewsletterTitle>Stay Connected</NewsletterTitle>
        <NewsletterText>
          Join our community of passionate readers and be the first to know about new releases, exclusive content, and special events.
        </NewsletterText>
        
        <EmailForm onSubmit={handleEmailSubmit} style={{ maxWidth: '400px', margin: '0 auto' }}>
          <EmailInput
            type="email"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            whileFocus={{ scale: 1.02 }}
            style={{ background: 'rgba(255, 255, 255, 0.2)', border: '2px solid rgba(255, 255, 255, 0.3)' }}
          />
          <EmailButton
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={emailSubmitted || isSubmitting}
            style={{ background: 'rgba(255, 255, 255, 0.2)', border: '2px solid white' }}
          >
            {emailSubmitted ? '✓ Subscribed!' : isSubmitting ? 'Sending...' : 'Subscribe'}
          </EmailButton>
        </EmailForm>
      </NewsletterContent>
      </div>
    </NewsletterSection>
    
    {/* Verification Modal */}
    <VerificationModal
      isOpen={showVerificationModal}
      onClose={() => setShowVerificationModal(false)}
      email={pendingEmail}
      onVerificationSuccess={handleVerificationSuccess}
    />
    
    {/* Gallery Modal */}
    {showGalleryModal && selectedGalleryImage && (
      <GalleryModal
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setShowGalleryModal(false)}
      >
        <GalleryModalContent
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <GalleryModalCloseButton onClick={() => setShowGalleryModal(false)}>
            ×
          </GalleryModalCloseButton>
          <GalleryModalImage src={`${API}${selectedGalleryImage.image}`} />
          <GalleryModalInfo>
            <GalleryModalTitle>{selectedGalleryImage.bookTitle}</GalleryModalTitle>
            {selectedGalleryImage.bookDescription && (
              <GalleryModalDescription>{selectedGalleryImage.bookDescription}</GalleryModalDescription>
            )}
            <GalleryModalButton
              onClick={() => {
                setShowGalleryModal(false);
                navigate(`/book/${selectedGalleryImage.bookId}/${selectedGalleryImage.bookTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Book Details
            </GalleryModalButton>
          </GalleryModalInfo>
        </GalleryModalContent>
      </GalleryModal>
    )}
    
    {/* Notification Toast */}
    <NotificationToast
      notifications={notifications}
      onRemove={removeNotification}
    />
    </>
  );
};

export default Home;
